/**
 * Employee Evaluations Page
 * Trang quản lý đánh giá nhân viên hàng tuần
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ClipboardCheck,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Calendar,
  FileText,
  ChevronDown,
} from 'lucide-react';
import { useEmployeeEvaluations } from '@/hooks/useEmployeeEvaluations';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useSupabaseTimePeriods } from '@/hooks/useSupabasePeriods';
import {
  EmployeeEvaluation,
  EvaluationFormData,
  EvaluationDetailFormData,
  EmployeeEvaluationWithDetails,
} from '@/types/evaluation';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { vi } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const EmployeeEvaluationsPage: React.FC = () => {
  const {
    evaluations,
    isLoading: evaluationsLoading,
    error: evaluationsError,
    createEvaluation,
    updateEvaluation,
    deleteEvaluation,
    getEvaluationWithDetails,
    saveEvaluationDetail,
  } = useEmployeeEvaluations();

  const { activeTeamMembers, isLoading: membersLoading } = useTeamMembers();
  
  const { data: timePeriods, isLoading: periodsLoading } = useSupabaseTimePeriods({
    period_type: 'Weekly',
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState<EmployeeEvaluationWithDetails | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedEvaluationId, setExpandedEvaluationId] = useState<string | null>(null);
  const [expandedEvaluationDetails, setExpandedEvaluationDetails] = useState<EmployeeEvaluationWithDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [formData, setFormData] = useState<EvaluationFormData>({
    evaluation_name: '',
    period_id: '',
    week_start_date: '',
    week_end_date: '',
    status: 'draft',
    notes: '',
  });

  const [evaluationDetails, setEvaluationDetails] = useState<{
    [key: string]: EvaluationDetailFormData;
  }>({});

  // Handle expand/collapse evaluation row
  const handleToggleExpand = async (evaluationId: string) => {
    if (expandedEvaluationId === evaluationId) {
      // Collapse if already expanded
      setExpandedEvaluationId(null);
      setExpandedEvaluationDetails(null);
    } else {
      // Expand and fetch details
      setExpandedEvaluationId(evaluationId);
      setIsLoadingDetails(true);
      try {
        const details = await getEvaluationWithDetails(evaluationId);
        setExpandedEvaluationDetails(details);
      } catch (err: any) {
        toast.error(err.message || 'Không thể tải chi tiết đánh giá');
        setExpandedEvaluationId(null);
      } finally {
        setIsLoadingDetails(false);
      }
    }
  };

  // Handle create new evaluation
  const handleCreate = () => {
    setFormData({
      evaluation_name: '',
      period_id: '',
      week_start_date: '',
      week_end_date: '',
      status: 'draft',
      notes: '',
    });
    setEvaluationDetails({});
    setIsCreateDialogOpen(true);
  };

  // Handle edit evaluation
  const handleEdit = async (evaluation: EmployeeEvaluation) => {
    try {
      const fullEvaluation = await getEvaluationWithDetails(evaluation.id);
      if (!fullEvaluation) {
        toast.error('Không thể tải chi tiết đánh giá');
        return;
      }

      setEditingEvaluation(fullEvaluation);
      setFormData({
        evaluation_name: fullEvaluation.evaluation_name,
        period_id: fullEvaluation.period_id,
        week_start_date: fullEvaluation.week_start_date,
        week_end_date: fullEvaluation.week_end_date,
        status: fullEvaluation.status,
        notes: fullEvaluation.notes || '',
      });

      // Load existing evaluation details
      const details: { [key: string]: EvaluationDetailFormData } = {};
      fullEvaluation.evaluation_details.forEach((detail) => {
        details[detail.team_member_id] = {
          team_member_id: detail.team_member_id,
          work_quality_score: detail.work_quality_score,
          work_quantity_score: detail.work_quantity_score,
          punctuality_score: detail.punctuality_score,
          teamwork_score: detail.teamwork_score,
          attitude_score: detail.attitude_score,
          comments: detail.comments,
          strengths: detail.strengths,
          improvements: detail.improvements,
        };
      });
      setEvaluationDetails(details);
      setIsEditDialogOpen(true);
    } catch (err: any) {
      toast.error(err.message || 'Có lỗi xảy ra');
    }
  };

  // Handle period change
  const handlePeriodChange = (periodId: string) => {
    const period = timePeriods?.find((p) => p.id === periodId);
    if (period && period.end_of_week_date) {
      const endDate = new Date(period.end_of_week_date);
      const startDate = startOfWeek(endDate, { weekStartsOn: 1 }); // Monday
      
      setFormData({
        ...formData,
        period_id: periodId,
        week_start_date: format(startDate, 'yyyy-MM-dd'),
        week_end_date: format(endDate, 'yyyy-MM-dd'),
        evaluation_name: `Đánh giá tuần ${period.period_name || periodId}`,
      });
    } else {
      setFormData({
        ...formData,
        period_id: periodId,
      });
    }
  };

  // Handle score change
  const handleScoreChange = (
    memberId: string,
    field: keyof EvaluationDetailFormData,
    value: any
  ) => {
    setEvaluationDetails((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || { team_member_id: memberId }),
        [field]: value,
      },
    }));
  };

  // Handle submit create
  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.evaluation_name.trim()) {
      toast.error('Vui lòng nhập tên đánh giá');
      return;
    }

    if (!formData.period_id) {
      toast.error('Vui lòng chọn tuần đánh giá');
      return;
    }

    setIsSaving(true);

    try {
      const newEvaluation = await createEvaluation(formData);
      if (!newEvaluation) {
        throw new Error('Không thể tạo đánh giá');
      }

      // Save evaluation details
      const detailsToSave = Object.values(evaluationDetails).filter(
        (detail) => detail.team_member_id
      );

      for (const detail of detailsToSave) {
        await saveEvaluationDetail(newEvaluation.id, detail);
      }

      toast.success('Tạo đánh giá thành công');
      setIsCreateDialogOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Có lỗi xảy ra');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle submit edit
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingEvaluation) return;

    setIsSaving(true);

    try {
      await updateEvaluation(editingEvaluation.id, formData);

      // Save evaluation details
      const detailsToSave = Object.values(evaluationDetails).filter(
        (detail) => detail.team_member_id
      );

      for (const detail of detailsToSave) {
        await saveEvaluationDetail(editingEvaluation.id, detail);
      }

      toast.success('Cập nhật đánh giá thành công');
      setIsEditDialogOpen(false);
      setEditingEvaluation(null);
    } catch (err: any) {
      toast.error(err.message || 'Có lỗi xảy ra');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (evaluation: EmployeeEvaluation) => {
    if (!confirm(`Bạn có chắc muốn xóa đánh giá "${evaluation.evaluation_name}"?`)) {
      return;
    }

    try {
      await deleteEvaluation(evaluation.id);
      toast.success('Xóa đánh giá thành công');
    } catch (err: any) {
      toast.error(err.message || 'Không thể xóa đánh giá');
    }
  };

  // Render score input with number input (0-10 scale)
  const renderScoreInput = (
    memberId: string,
    field: keyof EvaluationDetailFormData,
    label: string
  ) => {
    const value = evaluationDetails[memberId]?.[field] as number | undefined;
    return (
      <div className="grid grid-cols-12 gap-2 items-center">
        <Label className="col-span-8 text-sm">{label}</Label>
        <Input
          type="number"
          min="0"
          max="10"
          value={value || ''}
          onChange={(e) => {
            const val = parseInt(e.target.value);
            if (!isNaN(val) && val >= 0 && val <= 10) {
              handleScoreChange(memberId, field, val);
            } else if (e.target.value === '') {
              handleScoreChange(memberId, field, undefined);
            }
          }}
          className="col-span-4 text-center"
          placeholder="0-10"
        />
      </div>
    );
  };

  // Calculate total score for a member
  const calculateTotalScore = (memberId: string): number => {
    const detail = evaluationDetails[memberId];
    if (!detail) return 0;
    
    const scores = [
      detail.work_quality_score,
      detail.work_quantity_score,
      detail.punctuality_score,
      detail.teamwork_score,
      detail.attitude_score,
    ].filter(score => score !== undefined) as number[];
    
    return scores.reduce((sum, score) => sum + score, 0);
  };

  // Evaluation criteria definitions
  const evaluationCriteria = [
    {
      number: 1,
      title: 'Chủ động & tính thân trách nhiệm',
      description: '(Tự giác nhận việc, theo dõi tiến độ, nhắc lạ công việc quan trọng. Khi có lỗi, có dám nhận trách nhiệm và tìm cách khắc phục; không đổ lỗi, quản lý tốt nhiệm vụ của mình phụ trách.)',
    },
    {
      number: 2,
      title: 'Kỷ luật & tuân thủ quy định',
      description: 'Đi làm đúng giờ, tuân thủ nội bộ quy - an toàn – 5S, nghỉ có phép, không làm việc riêng, không rượu bia, hỗ trước hay tệ nạn khác.',
    },
    {
      number: 3,
      title: 'Đúng hạn & tuân thủ kế hoạch',
      description: 'Nhiêm vụ có hoàn thành đúng deadline? Có báo trước khi biết chắc bị trễ hạn, hay để sát giờ mới báo?',
    },
    {
      number: 4,
      title: 'Khả năng giải quyết vấn đề',
      description: 'Khi gặp sự cố có tự tìm cách xử lý trọng phạm vi cho phép? Có báo đề xuất phương án cho cấp trên, hay chỉ báo các vấn đề?',
    },
    {
      number: 5,
      title: 'Cải tiến & đề xuất hóa công việc',
      description: 'Trong tuần có đề xuất cách làm nhanh hơn, giảm lỗi, tiết kiệm chi phương nào? Có áp dụng lại các bài học thủ trước để tăm tối hơn không?',
    },
  ];

  if (evaluationsLoading || membersLoading || periodsLoading) {
    return (
      <div className="mx-auto px-3 sm:px-6 sm:max-w-6xl py-6 space-y-6">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Đánh giá nhân viên</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto px-3 sm:px-6 sm:max-w-6xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Đánh giá nhân viên</h1>
            <p className="text-sm text-gray-500 mt-1">
              Quản lý đánh giá nhân viên hàng tuần
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Tạo đánh giá mới
        </Button>
      </div>

      {/* Error Alert */}
      {evaluationsError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{evaluationsError}</AlertDescription>
        </Alert>
      )}

      {/* Warning if no team members */}
      {activeTeamMembers.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Bạn chưa có nhân viên nào trong team. Vui lòng thêm nhân viên trước khi tạo đánh giá.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tổng số đánh giá</p>
                <p className="text-2xl font-bold">{evaluations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Edit className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Đang soạn thảo</p>
                <p className="text-2xl font-bold">
                  {evaluations.filter((e) => e.status === 'draft').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ClipboardCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Đã hoàn thành</p>
                <p className="text-2xl font-bold">
                  {evaluations.filter((e) => e.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Evaluations List */}
      <Card>
        <CardHeader>
          <CardTitle>Danh sách đánh giá</CardTitle>
        </CardHeader>
        <CardContent>
          {evaluations.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Chưa có đánh giá nào</p>
              <Button onClick={handleCreate} variant="outline" disabled={activeTeamMembers.length === 0}>
                <Plus className="h-4 w-4 mr-2" />
                Tạo đánh giá đầu tiên
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="py-2 px-3">Tên đánh giá</TableHead>
                    <TableHead className="py-2 px-3">Tuần</TableHead>
                    <TableHead className="py-2 px-3">Thời gian</TableHead>
                    <TableHead className="py-2 px-3">Người đánh giá</TableHead>
                    <TableHead className="py-2 px-3">Trạng thái</TableHead>
                    <TableHead className="py-2 px-3">Ngày tạo</TableHead>
                    <TableHead className="text-right py-2 px-3">Thao tác</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evaluations.map((evaluation) => (
                    <React.Fragment key={evaluation.id}>
                      <TableRow 
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleToggleExpand(evaluation.id)}
                      >
                        <TableCell className="font-medium py-2 px-3">
                          <div className="flex items-center gap-2">
                            <ChevronDown 
                              className={cn(
                                "h-4 w-4 transition-transform",
                                expandedEvaluationId === evaluation.id && "rotate-180"
                              )}
                            />
                            {evaluation.evaluation_name}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 px-3">{evaluation.period_id}</TableCell>
                        <TableCell className="py-2 px-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-3 w-3 text-gray-400" />
                            {format(new Date(evaluation.week_start_date), 'dd/MM', { locale: vi })}
                            {' - '}
                            {format(new Date(evaluation.week_end_date), 'dd/MM/yyyy', { locale: vi })}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 px-3">{evaluation.evaluator_name || 'N/A'}</TableCell>
                        <TableCell className="py-2 px-3">
                          {evaluation.status === 'completed' ? (
                            <Badge variant="default" className="bg-green-100 text-green-800 py-0 px-2 text-xs">
                              Hoàn thành
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="py-0 px-2 text-xs">Nháp</Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-2 px-3">
                          {format(new Date(evaluation.created_at), 'dd/MM/yyyy HH:mm', { locale: vi })}
                        </TableCell>
                        <TableCell className="text-right py-2 px-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(evaluation)}
                              className="h-7 w-7 p-0"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(evaluation)}
                              className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Details Row */}
                      {expandedEvaluationId === evaluation.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-gray-50 p-4">
                            {isLoadingDetails ? (
                              <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              </div>
                            ) : expandedEvaluationDetails ? (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-base text-center mb-2">
                                  Chi tiết đánh giá
                                </h4>
                                
                                {/* Details Table */}
                                <div className="border rounded-lg overflow-hidden bg-white">
                                  <table className="w-full border-collapse text-sm">
                                    <thead>
                                      <tr className="bg-gray-100 border-b">
                                        <th className="border py-2 px-2 text-xs font-semibold text-left min-w-[120px]">
                                          Nhân viên
                                        </th>
                                        <th className="border py-2 px-2 text-xs font-semibold text-center min-w-[85px]">
                                          Chủ động và<br/>trách nhiệm
                                        </th>
                                        <th className="border py-2 px-2 text-xs font-semibold text-center min-w-[85px]">
                                          Kỷ luật và<br/>tuân thủ
                                        </th>
                                        <th className="border py-2 px-2 text-xs font-semibold text-center min-w-[85px]">
                                          Đúng hạn và<br/>có kế hoạch
                                        </th>
                                        <th className="border py-2 px-2 text-xs font-semibold text-center min-w-[85px]">
                                          Khả năng giải<br/>quyết vấn đề
                                        </th>
                                        <th className="border py-2 px-2 text-xs font-semibold text-center min-w-[85px]">
                                          Có sáng kiến<br/>và ý tưởng
                                        </th>
                                        <th className="border py-2 px-2 text-xs font-semibold text-center bg-blue-50 min-w-[70px]">
                                          Tổng<br/>điểm
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedEvaluationDetails.evaluation_details.length > 0 ? (
                                        expandedEvaluationDetails.evaluation_details.map((detail) => (
                                          <tr key={detail.id} className="hover:bg-gray-50">
                                            <td className="border py-1.5 px-2">
                                              <div className="font-medium text-sm">
                                                {detail.team_member?.employee_name || 'N/A'}
                                              </div>
                                              {detail.team_member?.position && (
                                                <div className="text-xs text-gray-500">
                                                  {detail.team_member.position}
                                                </div>
                                              )}
                                            </td>
                                            <td className="border py-1.5 px-2 text-center font-medium">
                                              {detail.work_quality_score ?? '-'}
                                            </td>
                                            <td className="border py-1.5 px-2 text-center font-medium">
                                              {detail.work_quantity_score ?? '-'}
                                            </td>
                                            <td className="border py-1.5 px-2 text-center font-medium">
                                              {detail.punctuality_score ?? '-'}
                                            </td>
                                            <td className="border py-1.5 px-2 text-center font-medium">
                                              {detail.teamwork_score ?? '-'}
                                            </td>
                                            <td className="border py-1.5 px-2 text-center font-medium">
                                              {detail.attitude_score ?? '-'}
                                            </td>
                                            <td className="border py-1.5 px-2 text-center bg-blue-50">
                                              <div className="font-bold text-base text-blue-700">
                                                {detail.total_score ?? 0}
                                              </div>
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan={7} className="border py-4 px-2 text-center text-gray-500 text-sm">
                                            Chưa có chi tiết đánh giá
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                {/* Notes Section */}
                                {expandedEvaluationDetails.notes && (
                                  <div className="mt-3 p-3 bg-white border rounded-lg">
                                    <h5 className="font-semibold mb-1.5 text-sm">Ghi chú:</h5>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                      {expandedEvaluationDetails.notes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                Không có chi tiết đánh giá
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmitCreate}>
            <DialogHeader>
              <DialogTitle>Tạo đánh giá mới</DialogTitle>
              <DialogDescription>
                Chọn tuần đánh giá và đánh giá từng nhân viên
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-6 py-4">
              {/* Basic Info */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="period_id">
                    Tuần đánh giá <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.period_id}
                    onValueChange={handlePeriodChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tuần" />
                    </SelectTrigger>
                    <SelectContent>
                      {timePeriods?.map((period) => (
                        <SelectItem key={period.id} value={period.id}>
                          {period.period_name || period.id} -{' '}
                          {period.end_of_week_date &&
                            format(new Date(period.end_of_week_date), 'dd/MM/yyyy', { locale: vi })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="evaluation_name">
                    Tên đánh giá <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="evaluation_name"
                    value={formData.evaluation_name}
                    onChange={(e) =>
                      setFormData({ ...formData, evaluation_name: e.target.value })
                    }
                    placeholder="Đánh giá tuần 15"
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="notes">Ghi chú</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder="Ghi chú về đánh giá..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Employee Evaluations Table */}
              {activeTeamMembers.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-center">FORM ĐÁNH GIÁ NHÂN VIÊN</h3>
                  
                  {/* Evaluation Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100 border-b">
                            <th className="border p-2 text-sm font-semibold text-center min-w-[150px]">
                              Nhân viên
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Chủ động và<br/>trách nhiệm
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Kỷ luật và<br/>tuân thủ
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Đúng hạn và<br/>có kế hoạch
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Khả năng giải<br/>quyết vấn đề
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Có sáng kiến<br/>và ý tưởng
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center bg-blue-50 min-w-[100px]">
                              Tổng<br/>điểm
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeTeamMembers.map((member) => (
                            <tr key={member.id} className="hover:bg-gray-50">
                              <td className="border p-2">
                                <div className="font-medium">{member.employee_name}</div>
                                {member.position && (
                                  <div className="text-xs text-gray-500">{member.position}</div>
                                )}
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.work_quality_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'work_quality_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'work_quality_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.work_quantity_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'work_quantity_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'work_quantity_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.punctuality_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'punctuality_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'punctuality_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.teamwork_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'teamwork_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'teamwork_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.attitude_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'attitude_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'attitude_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center bg-blue-50">
                                <div className="font-bold text-lg">
                                  {calculateTotalScore(member.id)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Evaluation Criteria Notes */}
                  <div className="mt-6 border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold mb-3">Ghi chú các tiêu chí:</h4>
                    <div className="space-y-3">
                      {evaluationCriteria.map((criteria) => (
                        <div key={criteria.number} className="flex gap-2">
                          <div className="font-medium min-w-[20px]">{criteria.number}/</div>
                          <div className="flex-1">
                            <span className="font-medium">{criteria.title}</span>
                            <div className="text-sm text-gray-600 mt-1 italic">
                              {criteria.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Comments Section */}
                  <div className="grid gap-4 mt-4">
                    <div className="grid gap-2">
                      <Label>Ghi chú chung cho kỳ đánh giá</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData({ ...formData, notes: e.target.value })
                        }
                        placeholder="Nhận xét chung về kỳ đánh giá này..."
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={isSaving}
              >
                Hủy
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFormData({ ...formData, status: 'draft' });
                  handleSubmitCreate(new Event('submit') as any);
                }}
                disabled={isSaving}
              >
                Lưu nháp
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, status: 'completed' });
                  handleSubmitCreate(new Event('submit') as any);
                }}
                disabled={isSaving}
              >
                {isSaving ? 'Đang lưu...' : 'Hoàn thành'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - Similar structure to Create Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmitEdit}>
            <DialogHeader>
              <DialogTitle>Chỉnh sửa đánh giá</DialogTitle>
              <DialogDescription>
                Cập nhật thông tin đánh giá và điểm số
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-6 py-4">
              {/* Basic Info - Same as Create */}
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit_period_id">
                    Tuần đánh giá <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.period_id}
                    onValueChange={handlePeriodChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tuần" />
                    </SelectTrigger>
                    <SelectContent>
                      {timePeriods?.map((period) => (
                        <SelectItem key={period.id} value={period.id}>
                          {period.period_name || period.id} -{' '}
                          {period.end_of_week_date &&
                            format(new Date(period.end_of_week_date), 'dd/MM/yyyy', { locale: vi })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="edit_evaluation_name">
                    Tên đánh giá <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="edit_evaluation_name"
                    value={formData.evaluation_name}
                    onChange={(e) =>
                      setFormData({ ...formData, evaluation_name: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="edit_notes">Ghi chú</Label>
                  <Textarea
                    id="edit_notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={2}
                  />
                </div>
              </div>

              {/* Employee Evaluations Table - Same as Create */}
              {activeTeamMembers.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg text-center">FORM ĐÁNH GIÁ NHÂN VIÊN</h3>
                  
                  {/* Evaluation Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-100 border-b">
                            <th className="border p-2 text-sm font-semibold text-center min-w-[150px]">
                              Nhân viên
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Chủ động và<br/>trách nhiệm
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Kỷ luật và<br/>tuân thủ
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Đúng hạn và<br/>có kế hoạch
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Khả năng giải<br/>quyết vấn đề
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center min-w-[120px]">
                              Có sáng kiến<br/>và ý tưởng
                            </th>
                            <th className="border p-2 text-sm font-semibold text-center bg-blue-50 min-w-[100px]">
                              Tổng<br/>điểm
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeTeamMembers.map((member) => (
                            <tr key={member.id} className="hover:bg-gray-50">
                              <td className="border p-2">
                                <div className="font-medium">{member.employee_name}</div>
                                {member.position && (
                                  <div className="text-xs text-gray-500">{member.position}</div>
                                )}
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.work_quality_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'work_quality_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'work_quality_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.work_quantity_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'work_quantity_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'work_quantity_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.punctuality_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'punctuality_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'punctuality_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.teamwork_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'teamwork_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'teamwork_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  max="10"
                                  value={evaluationDetails[member.id]?.attitude_score || ''}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 10) {
                                      handleScoreChange(member.id, 'attitude_score', val);
                                    } else if (e.target.value === '') {
                                      handleScoreChange(member.id, 'attitude_score', undefined);
                                    }
                                  }}
                                  className="w-20 text-center mx-auto text-sm h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="0-10"
                                />
                              </td>
                              <td className="border p-2 text-center bg-blue-50">
                                <div className="font-bold text-lg">
                                  {calculateTotalScore(member.id)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Evaluation Criteria Notes */}
                  <div className="mt-6 border rounded-lg p-4 bg-gray-50">
                    <h4 className="font-semibold mb-3">Ghi chú các tiêu chí:</h4>
                    <div className="space-y-3">
                      {evaluationCriteria.map((criteria) => (
                        <div key={criteria.number} className="flex gap-2">
                          <div className="font-medium min-w-[20px]">{criteria.number}/</div>
                          <div className="flex-1">
                            <span className="font-medium">{criteria.title}</span>
                            <div className="text-sm text-gray-600 mt-1 italic">
                              {criteria.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Additional Comments Section */}
                  <div className="grid gap-4 mt-4">
                    <div className="grid gap-2">
                      <Label>Ghi chú chung cho kỳ đánh giá</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData({ ...formData, notes: e.target.value })
                        }
                        placeholder="Nhận xét chung về kỳ đánh giá này..."
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                disabled={isSaving}
              >
                Hủy
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setFormData({ ...formData, status: 'draft' });
                  handleSubmitEdit(new Event('submit') as any);
                }}
                disabled={isSaving}
              >
                Lưu nháp
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, status: 'completed' });
                  handleSubmitEdit(new Event('submit') as any);
                }}
                disabled={isSaving}
              >
                {isSaving ? 'Đang lưu...' : 'Hoàn thành'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeEvaluationsPage;

